import { FC, useState } from 'react';
import { Box, Center } from '@chakra-ui/react';
import { Feature } from '@shinetools/pricing-plan-library';

import { useCurrentPricingPlan } from 'common/hooks/useCurrentPricingPlan';
import Button from 'components/_core/Button';
import SunshineCard from 'components/_core/SunshineCard';
import Text from 'components/_core/Text';
import { CatalogItemUpdateModal } from 'features/Work/components/CatalogItemModals';

import {
  CatalogItem,
  CatalogItemsPageInfo,
} from '../../../../../../hooks/useCatalogItemsQuery';
import { BlurryUpsellCard } from './components/BlurryUpsellCard';
import CatalogItemCard from './components/CatalogItemCard';
import DeleteModal from './components/DeleteModal';
import EmptyView from './components/EmptyView';
import ItemGrid from './components/ItemGrid';
import UpsellCard from './components/UpsellCard';
import locales from './locales';

interface BodyProps {
  catalogItems: CatalogItem[];
  pageInfo: CatalogItemsPageInfo | undefined;
  isFetchingMore: boolean;
  onFetchMore: () => void;
  onCreateItem: () => void;
}

const Body: FC<BodyProps> = ({
  catalogItems,
  isFetchingMore,
  onCreateItem,
  onFetchMore,
  pageInfo,
}) => {
  const { isFeatureAvailable } = useCurrentPricingPlan();
  const isItemsCatalogAvailable = isFeatureAvailable(
    Feature.InvoicingItemsCatalog,
  );

  const [updatedCatalogItem, setUpdatedCatalogItem] =
    useState<CatalogItem | null>(null);
  const [deletedCatalogItemId, setDeletedCatalogItemId] = useState<
    string | null
  >(null);

  if (!isItemsCatalogAvailable) {
    return catalogItems.length === 0 ? <UpsellCard /> : <BlurryUpsellCard />;
  }

  if (catalogItems.length === 0) {
    return (
      <EmptyView
        isSearchResult={Boolean(pageInfo?.totalCountWithoutFilters)}
        onCreateItem={onCreateItem}
      />
    );
  }

  return (
    <>
      <Box paddingBottom="space-8" paddingX="space-16">
        <ItemGrid>
          <Text size="xs" variant="light">
            {locales.name}
          </Text>

          <Text size="xs" variant="light">
            {locales.type}
          </Text>

          <Text align="right" size="xs" variant="light">
            {locales.price}
          </Text>

          <Text align="right" size="xs" variant="light">
            {locales.vat}
          </Text>
        </ItemGrid>
      </Box>

      <SunshineCard.Group>
        {catalogItems.map((catalogItem) => (
          <CatalogItemCard
            catalogItem={catalogItem}
            key={catalogItem.invoiceCatalogItemId}
            onClickDelete={(catalogItemId) =>
              setDeletedCatalogItemId(catalogItemId)
            }
            onClickUpdate={() => setUpdatedCatalogItem(catalogItem)}
          />
        ))}
      </SunshineCard.Group>

      {pageInfo?.hasNextPage ? (
        <Center paddingTop="space-32">
          <Button
            icon="arrow-down"
            isLoading={isFetchingMore}
            onClick={onFetchMore}
            variant="secondary"
          >
            {locales.seeMore}
          </Button>
        </Center>
      ) : null}

      {updatedCatalogItem ? (
        <CatalogItemUpdateModal
          catalogItem={updatedCatalogItem}
          isOpen
          onClickDelete={(catalogItemId) => {
            setUpdatedCatalogItem(null);
            setDeletedCatalogItemId(catalogItemId);
          }}
          onClose={() => setUpdatedCatalogItem(null)}
          returnFocusOnClose={false}
        />
      ) : null}

      {deletedCatalogItemId ? (
        <DeleteModal
          catalogItemId={deletedCatalogItemId}
          isOpen
          onClose={() => setDeletedCatalogItemId(null)}
          returnFocusOnClose={false}
        />
      ) : null}
    </>
  );
};

export default Body;
